import {
    type ReactNode,
    useState,
    Fragment,
    type FC
} from "react";
import {
    Pressable,
    View
} from "react-native";
import type IStepperSummaryProps from "./type";
import stylesheet, {
    STEPPER_SUMMARY_TITLE_VARIANT,
    useStyles
} from "./stylesheet";
import {
    getStepperSummaryTexts
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import type {
    StepperPartContentProps,
    StepperContextType
} from "../../type";
import {
    TriangleAlert as TriangleAlertIcon
} from "lucide-react-native";
import {
    withStepperContext,
    StepperContext
} from "../../context";
import ActionSheet from "../../../bottomSheet/components/actionSheet";
import StateLayer from "../../../stateLayer";
import Progress from "../../../progress";
import StepperList from "../list";
import Icon from "../../../icon";
import Text from "../../../text";

const StepperSummaryContent: FC<StepperPartContentProps<IStepperSummaryProps>> = ({
    customTheme: customThemeProp,
    context,
    style,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        shapeConfigs,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context.customLocalize);

    const [
        isSheetActive,
        setIsSheetActive
    ] = useState(false);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState();

    const texts = getStepperSummaryTexts({
        activeLocale: context.activeLocale,
        activeIndex: context.activeIndex,
        steps: context.steps,
        localize
    });

    const isOpenable = context.steps.some((item) => item.isPressable);

    const {
        container: containerDynamicStyle,
        segment: segmentDynamicStyle,
        surface: surfaceDynamicStyle,
        label: labelDynamicStyle
    } = useStyles({
        isFocusVisible: isOpenable && isFocusVisible,
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    });

    const handleSheetClosed = () => {
        setIsSheetActive(false);
    };

    const handleOpen = () => {
        setIsSheetActive(true);
    };

    const sheetContext: StepperContextType = {
        ...context,
        onStepPress: (index, step) => {
            setIsSheetActive(false);

            context.onStepPress?.(index, step);
        },
        orientation: "vertical",
        isInSheet: true
    };

    const labelColor = texts.errorCount ? colors.content.text.danger : undefined;

    const renderTitle = () => {
        return <Fragment>
            {`${texts.stepOf} · `}
            <Text
                customColor={labelColor ?? colors.content.text.high}
                variant={STEPPER_SUMMARY_TITLE_VARIANT}
                customTheme={customTheme}
            >
                {texts.title}
            </Text>
            {texts.errorText ? ` · ${texts.errorText}` : null}
        </Fragment>;
    };

    const renderSegments = () => {
        return context.steps.map((item) => {
            if(item.status === "error") {
                return <View
                    style={[
                        stylesheet.segment,
                        segmentDynamicStyle
                    ]}
                    key={item.key}
                />;
            }

            return <Progress.Step
                isDone={item.index <= context.activeIndex}
                key={item.key}
            />;
        });
    };

    const renderProgress = (): ReactNode => {
        return <Progress
            valueFormatter={() => texts.accessibleText}
            customLocalize={context.customLocalize}
            value={context.activeIndex + 1}
            steps={context.steps.length}
            title={texts.accessibleText}
            customTheme={customTheme}
            isValueVisible={false}
            variant="segmented"
            size="small"
        >
            <Progress.Header>
                <View
                    style={[
                        stylesheet.label,
                        labelDynamicStyle
                    ]}
                >
                    {
                        texts.errorCount ?
                            <Icon
                                customColor={colors.content.text.danger}
                                customTheme={customTheme}
                                icon={TriangleAlertIcon}
                                size={iconSizes.small}
                            />
                        :
                            null
                    }
                    <Progress.Title>
                        {renderTitle()}
                    </Progress.Title>
                </View>
                <Progress.Value>
                    {texts.valueText}
                </Progress.Value>
            </Progress.Header>
            <Progress.Track>
                {renderSegments()}
            </Progress.Track>
        </Progress>;
    };

    const renderSheet = () => {
        if(!isOpenable) {
            return null;
        }

        return <ActionSheet
            description={localize("stepper-steps-desc")}
            customLocalize={context.customLocalize}
            title={localize("stepper-steps-title")}
            cancelTitle={localize("close")}
            onClosed={handleSheetClosed}
            customTheme={customTheme}
            isActive={isSheetActive}
        >
            <StepperContext.Provider
                value={sheetContext}
            >
                <StepperList />
            </StepperContext.Provider>
        </ActionSheet>;
    };

    if(!isOpenable) {
        return <View
            {...props}
            style={[
                style,
                stylesheet.container
            ]}
        >
            {renderProgress()}
        </View>;
    }

    return <Fragment>
        <Pressable
            {...props}
            {...interactionHandlers}
            aria-label={`${texts.accessibleText}. ${localize("stepper-show-steps")}`}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            onPress={handleOpen}
            role="button"
        >
            <View
                style={[
                    stylesheet.surface,
                    surfaceDynamicStyle
                ]}
            >
                <StateLayer
                    borderRadius={radiuses.actions}
                    customTheme={customTheme}
                    overlayColor="default"
                    isHovered={isHovered}
                    isPressed={isPressed}
                />
                {renderProgress()}
            </View>
        </Pressable>
        {renderSheet()}
    </Fragment>;
};
export default withStepperContext("Summary", StepperSummaryContent);
